import React, { useEffect, useState } from "react";
import { Wrapper, TextWrapper, TextWrapperSpan, StyledImage } from "../../../constants/common/StyledGlobalConstants";
import CPNotification from "../../../pages/Login/CPNotification";
import PopOutIcon from "../../../assets/icons/External_link.svg";
import {
  setServiceDetails,
  setUpdateJob,
  setIsAssistanceRequired,
  resetContactDetailsPageData,
} from "../../../store/actions/schedulingportal/technicianAppointment";
import AdditionalService from "./AdditionalService/AdditionalService";
import AddAnotherService from "./AdditionalService/AddAnotherService";
import VehicleAssetDetails from "./VehicleAssetDetails";
import DiscardChangesModal from "../common/DiscardChangesModal";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "react-simple-snackbar";
import DeleteServiceModal from "./DeleteServiceModal";
import { SnackBarButton } from "../../cases/AddComment";
import "../../../constants/common/StyleOnlyForScreenReader.css";
import { getPendoId } from "../../../util/cfstPendoUtils";
import { DEVICE_BREAKPOINTS } from "../../../constants/appConstants";
import PropTypes from "prop-types";

const { TABLET } = DEVICE_BREAKPOINTS;

const VehicleAssetDetailsHeader = ({
  serviceId,
  setIsNextDisabled,
  isAdditionalService,
  setIsAdditionalService,
  stepperRef,
  isNextDisabled,
  windowSize,
  jobId,
  isAssistanceRequired,
  setIsFirstServiceEdited,
}) => {
  const [showSnackBar, setShowSnackBar] = useState({
    isOpen: false,
    action: "",
  });
  const [isEditVehicleAssetDetails, setIsEditVehicleAssetDetails] = useState({
    isOpen: false,
    index: -1,
  });

  const dispatch = useDispatch();
  const [isDiscardChange, setIsDiscardChange] = useState({ isOpen: false, toEditIndex: -1, toDeleteIndex: -1, toAddNewService: false });
  const { serviceDetails, updateJob } = useSelector((state) => state.TECHNICIANAPPOINTMENT);
  const [serviceType, setServiceType] = useState(serviceId);
  const [isAddAnotherService, setIsAddAnotherService] = useState(true);
  const [isDeleteservice, setIsDeleteservice] = useState({
    isDeleteservice: false,
    index: -1,
  });
  const [isFieldsEdited, setIsFieldsEdited] = useState(false);

  const [openSnackbar, closeSnackbar] = useSnackbar({
    position: "bottom-center",
    style: {
      borderRadius: "4px",
      boxShadow: "0px 1px 16px 0px rgba(0, 0, 0, 0.25)",
      backgroundColor: "#000000",
      fontFamily: "Neue Haas Grotesk Text Std",
      fontSize: "0.875rem",
      lineHeight: "1.25rem",
      marginBottom: "4rem",
    },
    closeStyle: {
      display: "none",
    },
  });

  function getSnackBarVerbiage(showAgentAssistanceRequired, showSnackBarAction) {
    if (showAgentAssistanceRequired && showSnackBarAction !== "deleted") {
      return "Agent assistance required";
    }
    if (showSnackBarAction === "saved") {
      return "Service saved";
    }
    return "Service deleted";
  };

  useEffect(() => {
    //Show Snackbar for 5 seconds when service is saved or deleted
    // Show Snackbar for 10 seconds when agent assistance is required
    const checkIsWarningIndex = isEditVehicleAssetDetails.isOpen ? isEditVehicleAssetDetails.index : serviceDetails.length - 1;
    const showAgentAssistanceRequired = serviceDetails[checkIsWarningIndex]?.isWarning && showSnackBar.action !== "deleted";
    const snackBarDisplayTime = showAgentAssistanceRequired ? 10000 : 5000;
    let snackBarVerbiage = getSnackBarVerbiage(showAgentAssistanceRequired, showSnackBar.action);
    showSnackBar.isOpen &&
      openSnackbar(
        <Wrapper
          fontFamily="neue-haas-grotesk-text"
          role="alert"
          tabIndex={0}
          display="grid"
          gridTemplateColumns={showAgentAssistanceRequired ? "auto auto" : "auto"}
          alignItems="center"
          aria-label={snackBarVerbiage}
        >
          {snackBarVerbiage}
          {showAgentAssistanceRequired && (
            <SnackBarButton
              padding="8px 16px"
              boxButton={true}
              fontSize="12px"
              buttonWidth={"auto"}
              buttonHeight="32px"
              id="view-details-button"
              onClick={handleViewDetails}
              fontPaddingLeft="0"
              fontPaddingRight="0"
              tabIndex="0"
            >
              VIEW DETAILS
            </SnackBarButton>
          )}
        </Wrapper>,
        snackBarDisplayTime
      );
    return () => {
      closeSnackbar();
    };
  }, [showSnackBar, serviceDetails]);

  const handleViewDetails = () => {
    closeSnackbar();
    let assistanceRequiredIndex = isEditVehicleAssetDetails.isOpen ? isEditVehicleAssetDetails.index : serviceDetails.length - 1;
    setIsEditVehicleAssetDetails({
      isOpen: assistanceRequiredIndex >= 0,
      index: assistanceRequiredIndex,
    });
  };

  const handleDiscardChangeModal = (discard) => {
    const resetDiscardChangeState = () => {
      setIsDiscardChange((prevState) => ({
        ...prevState,
        isOpen: false,
        toEditIndex: -1,
        toDeleteIndex: -1,
        toAddNewService: false,
      }));
    };

    const handleEditService = () => {
      if (isDiscardChange.toEditIndex >= 0) {
        setServiceType(serviceDetails[isDiscardChange.toEditIndex].serviceType === "Transfer" ? 1 : 2);
      }
      setIsEditVehicleAssetDetails({
        isOpen: isDiscardChange.toEditIndex >= 0,
        index: isDiscardChange.toEditIndex,
      });
    };

    const handleDeleteService = () => {
      setIsDeleteservice({
        isDeleteservice: true,
        index: isDiscardChange.toDeleteIndex,
      });
    };

    if (discard === "discard") {
      if (isDiscardChange.toAddNewService) {
        handleAnotherNewService();
        resetDiscardChangeState();
        return;
      }

      if (isEditVehicleAssetDetails.isOpen || isDiscardChange.toEditIndex >= 0) {
        handleEditService();
      }

      if (isDiscardChange.toDeleteIndex >= 0) {
        handleDeleteService();
      }

      !isAddAnotherService && setIsAddAnotherService(true);
      setIsNextDisabled(serviceDetails.some((service) => service.isError));
    } else if (isEditVehicleAssetDetails.isOpen) {
      setServiceType(serviceDetails[isEditVehicleAssetDetails.index].serviceType === "Transfer" ? 1 : 2);
    }

    resetDiscardChangeState();
  };

  // Edit the selected record on click of Edit in Additional Service
  const editVehicleDetails = (index) => {
    if (isEditVehicleAssetDetails.isOpen && isNextDisabled && isFieldsEdited) {
      //if an existing service is opened and edited, display discard modal
      setIsDiscardChange((prevState) => ({
        ...prevState,
        isOpen: true,
        toEditIndex: index !== isEditVehicleAssetDetails.index ? index : -1,
        toDeleteIndex: -1,
        toAddNewService: false,
      }));
      return;
    } else if (!isAddAnotherService) {
      // If a new service is opened and edited, discard modal will be displayed
      if (isFieldsEdited) {
        setIsDiscardChange((prevState) => ({ ...prevState, isOpen: true, toEditIndex: index, toDeleteIndex: -1, toAddNewService: false })); //display discard modal if add another service page is opened and edited
        return;
      }
      // If a new service is opened but not edited, close the new service form
      setIsAddAnotherService(true);
    }
    //if an existing service opened but not edited, open the current service to be edited
    setServiceType(serviceDetails[index].serviceType === "Transfer" ? 1 : 2);
    setIsEditVehicleAssetDetails({
      isOpen: true,
      index: index,
    });
  };

  // Delete the selected record on click of Delete in Additional Service
  const deleteVehicleDetails = (index) => {
    if (isEditVehicleAssetDetails.isOpen) {
      if (isNextDisabled) {
        //if an existing service is opened and edited, display discard modal
        setIsDiscardChange((prevState) => ({
          ...prevState,
          isOpen: true,
          toEditIndex: -1,
          toDeleteIndex: index,
          toAddNewService: false,
        }));
        return;
      }
      // If an existing service is opened but not edited, close the opened existing service
      setIsEditVehicleAssetDetails({
        isOpen: false,
        index: -1,
      });
    } else if (!isAddAnotherService) {
        if (isFieldsEdited) {
          // If a new service is opened and edited, discard modal will be displayed
          setIsDiscardChange((prevState) => ({ ...prevState, isOpen: true, toEditIndex: -1, toDeleteIndex: index, toAddNewService: false })); //display discard modal if add another service page is opened and edited
          return;
        }
        // If a new service is opened but not edited, close the form
        setIsAddAnotherService(true);
    }
    //open the delete modal to delete the selected record
    setIsDeleteservice({
      isDeleteservice: true,
      index: index,
    });
  };

  // Delete the selected record on click of Delete in Additional Service
  const deleteService = (index, toCancel = false) => {
    if (!toCancel) {
      let serviceDetailsList = [...serviceDetails],
        service = serviceDetailsList.filter((record, i) => i !== index),
        //fetch details of deleted service
        deletedService = serviceDetailsList.find((item, i) => i === index);
      //For update Job
      if (jobId) {
        let updatedPayload = updateJob.payload;
        let updatedServicesPayload = updateJob?.payload?.services;
        updatedServicesPayload?.forEach((item) => {
          if (item.type === deletedService?.serviceType.toLowerCase()) {
            const index = item?.servicesDetails?.findIndex((item) => item?.uiIndex === deletedService?.uiIndex && item.isDeleted === false);
            if (index !== -1) {
              //If the existing service is deleted set IsDeleted to true
              if (item?.servicesDetails[index]?.existingVehicle === null || item?.servicesDetails[index]?.existingVehicle?.id === null) {
                item.servicesDetails.splice(index, 1);
              } else {
                //If service is newly added, remove the service object
                item.servicesDetails[index].isDeleted = true;
              }
            }
          }
        });
        updatedPayload.services = updatedServicesPayload;
        dispatch(setUpdateJob({ payload: updatedPayload, isUpdated: true }));
      }
      serviceDetailsList = [...service];
      setIsDeleteservice({
        isDeleteservice: false,
        index: -1,
      });
      setIsNextDisabled(!(serviceDetailsList.length && serviceDetailsList.every((service) => !service.isError)));
      setIsAgentAssitanceRequired(serviceDetailsList);
      dispatch(setServiceDetails(serviceDetailsList));
      setShowSnackBar({
        isOpen: true,
        action: "deleted",
      });
      if (serviceDetailsList.length === 0) {
        setIsFirstServiceEdited(false);
        //reset the contact details page data ti intial state if it is filled already
        dispatch(resetContactDetailsPageData());
      }
    } else {
      setIsDeleteservice({
        isDeleteservice: false,
        index: -1,
      });
      setIsNextDisabled(!(serviceDetails.length && serviceDetails.every((service) => !service.isError)));
      setIsAgentAssitanceRequired(serviceDetails);
    }
  };

  const handleVehicleAssetDetails = (serviceCount) => {
    //return the VehicleAssetDetails component
    return (
      <VehicleAssetDetails
        serviceType={serviceType}
        setShowSnackBar={setShowSnackBar}
        isAdditionalService={isAdditionalService}
        setIsAdditionalService={setIsAdditionalService}
        stepperRef={stepperRef}
        isEditVehicleAssetDetails={isEditVehicleAssetDetails}
        setIsEditVehicleAssetDetails={setIsEditVehicleAssetDetails}
        isDiscardChange={isDiscardChange}
        setIsDiscardChange={setIsDiscardChange}
        isNextDisabled={isNextDisabled}
        setIsNextDisabled={setIsNextDisabled}
        setIsAddAnotherService={setIsAddAnotherService}
        isAddAnotherService={isAddAnotherService}
        serviceCount={serviceCount}
        jobId={jobId}
        isFieldsEdited={isFieldsEdited}
        setIsFieldsEdited={setIsFieldsEdited}
        setIsFirstServiceEdited={setIsFirstServiceEdited}
      />
    );
  };

  const handleAnotherService = (serviceId) => {
    setServiceType(serviceId);
    if (isEditVehicleAssetDetails.isOpen && isNextDisabled && isFieldsEdited) {
      setIsDiscardChange((prevState) => ({ ...prevState, isOpen: true, toEditIndex: -1, toDeleteIndex: -1, toAddNewService: true }));
      return;
    }
    handleAnotherNewService();
  };

  const handleAnotherNewService = () => {
    setIsNextDisabled(true);
    isEditVehicleAssetDetails.isOpen &&
      setIsEditVehicleAssetDetails({
        isOpen: false,
        index: -1,
      });
    isAddAnotherService && setIsAddAnotherService(false);
  };

  //function to set the agent assistance required flag
  const setIsAgentAssitanceRequired = (serviceDetailsList) => {
    if (serviceDetailsList.some((service) => service.isWarning)) {
      !isAssistanceRequired && dispatch(setIsAssistanceRequired(true));
    } else {
      isAssistanceRequired && dispatch(setIsAssistanceRequired(false));
    }
  };

  return (
    <Wrapper marginBottom={windowSize.width >= TABLET ? "5.375rem" : "2.813rem"}>
      {isDiscardChange.isOpen && <DiscardChangesModal handleDiscardChangeModal={handleDiscardChangeModal} />}
      {isDeleteservice.isDeleteservice && (
        <DeleteServiceModal isDeleteservice={isDeleteservice} setIsDeleteservice={setIsDeleteservice} deleteService={deleteService} />
      )}
      <Wrapper>
        <h1 className="sr-only" tabIndex="-1">
          Vehicle or asset details
        </h1>
        <TextWrapper
          alignContent="center"
          fontSize="2.5rem"
          fontFamily="neue-haas-grotesk-display"
          fontWeight="700"
          lineHeight="3rem"
          aria-hidden="true"
          tabIndex="-1"
        >
          Vehicle or asset details
        </TextWrapper>
        <TextWrapper fontSize="0.875rem" fontWeight="400" marginTop="1.5rem !important">
          You will have the chance to add multiple services to your appointment one at a time. If you have multiple locations, you must create a
          separate appointment for each.
        </TextWrapper>
      </Wrapper>
      <Wrapper marginTop="2rem" width="100%">
        <CPNotification isOpen={true} notificationType="info" closeButton={false} width="auto" height="auto">
          <TextWrapperSpan
            isLink
            aria-label="Fee information"
            role="link"
            tabIndex="0"
            color=""
            fontFamily="neue-haas-grotesk-text"
            fontSize="14px"
            fontWeight="400"
            lineHeight="1.25rem"
            textDecorationLine="underline"
            id={getPendoId("fee-information-link")}
            onClick={() => window.open("https://reveal-help.verizonconnect.com/hc/articles/17745278972435")}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") && window.open("https://reveal-help.verizonconnect.com/hc/articles/17745278972435")
            }
            style={{ cursor: "pointer" }}
          >
            {"Fee information"}
          </TextWrapperSpan>
          <a href="https://reveal-help.verizonconnect.com/hc/articles/17745278972435" target="_blank" aria-hidden="true" tabIndex="-1">
            <TextWrapperSpan verticalAlign="sub">
              <StyledImage
                width="1rem"
                height="1rem"
                marginLeft="3px"
                src={PopOutIcon}
                alt="External link"
                id={getPendoId("fee-information-link-icon")}
              />
            </TextWrapperSpan>
          </a>
          <TextWrapperSpan
            aria-label=" for Verizon Connect hardware appointments."
            fontSize="14px"
            fontWeight="400"
            fontFamily="neue-haas-grotesk-text"
            lineHeight="1.25rem"
          >
            {" for Verizon Connect hardware appointments."}
          </TextWrapperSpan>
        </CPNotification>
      </Wrapper>
      {!isAdditionalService ? (
        handleVehicleAssetDetails(-1)
      ) : (
        <>
          {isAssistanceRequired && (
            <Wrapper margin="1rem 0rem">
              <CPNotification isOpen={true} notificationType="warning" closeButton={false} height="auto">
                <TextWrapperSpan>Agent assistance required.</TextWrapperSpan>
              </CPNotification>
            </Wrapper>
          )}
          {serviceDetails?.length > 0 &&
            serviceDetails.map((record, index) => {
              let indexValue = `R${index}`;
              return (
                  <AdditionalService
                    key={indexValue}
                    index={index}
                    record={record}
                    isEditVehicleAssetDetails={isEditVehicleAssetDetails}
                    editVehicleDetails={editVehicleDetails}
                    deleteVehicleDetails={deleteVehicleDetails}
                    handleVehicleAssetDetails={handleVehicleAssetDetails}
                  />
              );
            })}
          <AddAnotherService
            serviceDetails={serviceDetails}
            handleAnotherService={handleAnotherService}
            handleVehicleAssetDetails={handleVehicleAssetDetails}
            isAddAnotherService={isAddAnotherService}
            setIsAddAnotherService={setIsAddAnotherService}
          />
        </>
      )}
    </Wrapper>
  );
};

VehicleAssetDetailsHeader.propTypes = {
  serviceId: PropTypes.any,
  setIsNextDisabled: PropTypes.func.isRequired,
  isAdditionalService: PropTypes.bool.isRequired,
  setIsAdditionalService: PropTypes.func.isRequired,
  stepperRef: PropTypes.any,
  isNextDisabled: PropTypes.bool.isRequired,
  windowSize: PropTypes.object.isRequired,
  jobId: PropTypes.any,
  isAssistanceRequired: PropTypes.bool.isRequired,
  setIsFirstServiceEdited: PropTypes.func.isRequired,
};

export default VehicleAssetDetailsHeader;